import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { QcsToggleButton } from '../common/basic/QcsToggleButton';
import { useTranslation } from 'react-i18next';
import { FC, useState } from 'react';
import { useAppDispatch } from '../../store';
import { QcsButton } from '../common/basic/QcsButton';
import { GridFilterItemContainer } from '../common/grid/GridFilterStyles';
import { visitationListActions, VisitationListFilter } from '../../store/entities/visitationList';
import { FormContainer } from '../common/form/FormContainer';
import { QcsCheckbox } from '../common/basic/QcsCheckbox';
import { QcsFormControlLabel } from '../common/basic/QcsFormControlLabel';
import { QcsDateTimePicker } from '../common/basic/QcsDateTimePicker';
import { StyledQcsPopover } from '../common/grid/GridFilterItem';

type ExitTimeMode = 'hasExited' | 'inAreal' | 'fromTo';

const MODE_OPTIONS: { mode: ExitTimeMode; labelKey: string }[] = [
  { mode: 'hasExited', labelKey: 'visitation.filterHasExited' },
  { mode: 'inAreal', labelKey: 'visitation.filterOnPremisesOnly' },
  { mode: 'fromTo', labelKey: 'visitation.filterFromTo' },
];

interface Props {
  query: VisitationListFilter;
}

export const VisitationFilterExitTime: FC<Props> = ({ query }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const dispatch = useAppDispatch();

  const currentMode: ExitTimeMode | null = query.hasExited ? 'hasExited' : query.inAreal ? 'inAreal' : query.exitTime ? 'fromTo' : null;

  const handleModeChange = (mode: ExitTimeMode | null) => {
    if (mode === currentMode) return;

    dispatch(
      visitationListActions.updateFilter({
        exitTime: mode === 'fromTo' ? { from: undefined, to: undefined } : undefined,
        hasExited: mode === 'hasExited' ? true : undefined,
        inAreal: mode === 'inAreal' ? true : undefined,
      }),
    );
  };

  const handleChangeTo = (date?: Date | null) => {
    let finalDate = date;

    // Set default time to 23:55 when initially selecting a date and time is midnight
    if (date && !query.exitTime?.to && date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
      finalDate = new Date(date);
      finalDate.setHours(23, 55, 0);
    }

    dispatch(
      visitationListActions.updateFilter({
        exitTime: { from: query.exitTime?.from, to: finalDate || undefined },
      }),
    );
  };

  return (
    <>
      <QcsToggleButton value="" selected={!!currentMode} onChange={(e) => setAnchorEl(e.currentTarget)}>
        {t('visitation.exitTime')}
        <ArrowDropDownIcon />
      </QcsToggleButton>
      <StyledQcsPopover anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <GridFilterItemContainer>
          <FormContainer>
            {MODE_OPTIONS.map(({ mode, labelKey }) => (
              <QcsFormControlLabel
                key={mode}
                control={<QcsCheckbox singleSelect checked={currentMode === mode} onChange={() => handleModeChange(mode)} />}
                label={t(labelKey)}
              />
            ))}
            {currentMode === 'fromTo' && (
              <>
                <QcsDateTimePicker
                  label={t('common.from')}
                  value={query.exitTime?.from ?? null}
                  onChange={(date) =>
                    dispatch(
                      visitationListActions.updateFilter({
                        exitTime: { from: date || undefined, to: query.exitTime?.to },
                      }),
                    )
                  }
                />
                <QcsDateTimePicker label={t('common.to')} value={query.exitTime?.to ?? null} onChange={handleChangeTo} />
              </>
            )}
          </FormContainer>
        </GridFilterItemContainer>
        <GridFilterItemContainer>
          <QcsButton disabled={!currentMode} onClick={() => handleModeChange(null)}>
            {t('common.clearFilter')}
          </QcsButton>
        </GridFilterItemContainer>
      </StyledQcsPopover>
    </>
  );
};
